import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  plainSeriesTemplateTransaction,
  type scheduledTransactionEditFormFactory,
  type ScheduledTransactionEditFormInput,
  type ScheduledTransactionEditSubmitPayload,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionEditForm';
import type { ScheduledTransactionType } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { splitItemsToArray } from 'app-components/flux/transactions/splitsHelpers';
import { toPlainIfImmutable } from 'app-components/utils/core/immutableHelpers';

import { useAppSelector } from 'hooks/redux';

import { FormProvider } from '../hooks/formHooks';
import { useScheduledTransactionEditViewModel } from '../hooks/useScheduledTransactionEditViewModel';
import type { ScheduledTransactionEditDialogProps } from '../types';
import ScheduledTransactionEditView from './ScheduledTransactionEditView';

type SubmitPayloadHandler = (payload: ScheduledTransactionEditSubmitPayload) => void;
type ScheduledTransactionEditFormManager = ReturnType<typeof scheduledTransactionEditFormFactory>;

type InnerProps = ScheduledTransactionEditDialogProps & {
  scheduledTransactionPlain: ScheduledTransactionType;
  submitPayloadRef: React.MutableRefObject<SubmitPayloadHandler | null>;
};

function ScheduledTransactionEditFormProviderInner(props: InnerProps): React.JSX.Element {
  const viewModel = useScheduledTransactionEditViewModel(props);

  return <ScheduledTransactionEditView viewModel={viewModel} />;
}

export default function ScheduledTransactionEditFormProvider(
  props: ScheduledTransactionEditDialogProps,
): React.JSX.Element {
  const {
    scheduledTransaction: scheduledTransactionProp,
    incomeInitial = false,
    transferInitial = false,
    fakeCreate,
  } = props;
  const currentDateString = useAppSelector(dateSelectors.getCurrentDateString);
  const splitSeriesFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  const managerRef = useRef<ScheduledTransactionEditFormManager | null>(null);
  const scheduledTransactionPlain = useMemo(
    () => toPlainIfImmutable(scheduledTransactionProp) as ScheduledTransactionType,
    [scheduledTransactionProp],
  );
  const seriesSplitTemplateFingerprint = useMemo(
    () =>
      String(splitItemsToArray(plainSeriesTemplateTransaction(scheduledTransactionPlain)?.split?.items ?? []).length),
    [scheduledTransactionPlain],
  );
  const submitPayloadRef = useRef<SubmitPayloadHandler | null>(null);

  const formInput = useMemo<ScheduledTransactionEditFormInput>(
    () => ({
      scheduledTransaction: scheduledTransactionProp,
      currentDateString,
      incomeInitial,
      transferInitial,
      fakeCreate,
      splitSeriesEnabled: Boolean(splitSeriesFf),
    }),
    [currentDateString, fakeCreate, incomeInitial, scheduledTransactionProp, splitSeriesFf, transferInitial],
  );

  const formInputFingerprint = useMemo(
    () =>
      JSON.stringify({
        scheduledTransaction: scheduledTransactionPlain,
        currentDateString,
        incomeInitial,
        transferInitial,
        fakeCreate,
        splitSeriesEnabled: Boolean(splitSeriesFf),
        seriesSplitTemplateFingerprint,
      }),
    [
      currentDateString,
      fakeCreate,
      incomeInitial,
      scheduledTransactionPlain,
      seriesSplitTemplateFingerprint,
      splitSeriesFf,
      transferInitial,
    ],
  );
  const previousFormInputFingerprintRef = useRef(formInputFingerprint);
  useEffect(() => {
    if (previousFormInputFingerprintRef.current === formInputFingerprint) {
      return;
    }
    previousFormInputFingerprintRef.current = formInputFingerprint;

    if (!managerRef.current?.isFormDirty()) {
      managerRef.current?.reinit(formInput);
    }
  }, [formInput, formInputFingerprint]);

  const formKey = `${scheduledTransactionPlain?.id || scheduledTransactionPlain?.clientId || 'new'}-${String(
    incomeInitial,
  )}-${String(transferInitial)}-${String(fakeCreate)}`;

  const submitPayload = useCallback(({ payload }: { payload: ScheduledTransactionEditSubmitPayload }) => {
    submitPayloadRef.current?.(payload);
  }, []);

  return (
    <FormProvider key={formKey} input={formInput} managerRef={managerRef} onSubmit={submitPayload}>
      <ScheduledTransactionEditFormProviderInner
        {...props}
        scheduledTransactionPlain={scheduledTransactionPlain}
        submitPayloadRef={submitPayloadRef}
      />
    </FormProvider>
  );
}
